/**
 * Editing a work-order pipeline **after** the type exists.
 *
 * `POST /config/types` has always written a whole pipeline in one transaction —
 * stages, transitions and the type together. What has never existed is any way
 * to change one afterwards, which is requirement 1.2's remaining half and the
 * reason the Configuration screen says "Editing stages afterwards is not built
 * yet". These are the request bodies for the four operations that are safe to
 * build today.
 *
 * ## What is deliberately NOT here, and why
 *
 * `tb_app` — the role the API connects as — holds a **column-level** UPDATE on
 * `work_order_stage`, and the list is exactly `(name, active, required_fields)`
 * (`packages/db/bootstrap/02-grants.sql`). `sequence`, `code`, `is_entry`,
 * `is_completed` and `is_closed` are **not** writable by the application at all,
 * on purpose: they are the shape of the state machine rather than its labels.
 * So there is no `reorder` body and no `setEntryStage` body here. Both would
 * need a grant change or a `SECURITY DEFINER` function, and inventing a body for
 * an operation the database refuses would only move the failure to runtime.
 *
 * The same reading gives the one asymmetry in the transition bodies: there is no
 * delete. `tb_app` holds no DELETE on either table, so removing an edge is
 * `active = false` — which is also the honest operation, because a work order's
 * history in `work_order_status_event` points at edges that were taken.
 */
import { z } from "zod";
import { idempotencyKey, strictObject } from "./requests.js";
import { userRoles } from "./vocabulary.js";

/**
 * `work_order_stage.code`, matching `createWorkOrderTypeBody.code`.
 *
 * Unique per type through `work_order_stage_type_code_uq`, which has **no
 * `active` predicate** — so a retired stage still owns its code and a new stage
 * cannot reuse it.
 */
export const stageCode = z
  .string()
  .trim()
  .min(2)
  .max(40)
  .regex(/^[a-z][a-z0-9_]*$/, "lower-case letters, digits and underscores, starting with a letter");

export const stageName = z.string().trim().min(1).max(120);

/**
 * One `required_fields` entry: a `builtin:` key or a custom-field key.
 *
 * The **format** is checked here and **membership** is checked by the API, which
 * is the only side that can see which custom fields this type has published.
 * That split matters: `transition_work_order` treats an unrecognised `builtin:`
 * key as unsatisfiable (it fails closed), and there is no foreign key from
 * `required_fields` to `custom_field_def.key` at all — so a key that merely
 * *looks* right is accepted by the database and then blocks live work orders.
 */
export const requiredFieldKey = z
  .string()
  .trim()
  .regex(
    /^(builtin:[a-z][a-z0-9_]*|[a-z][a-z0-9_]*)$/,
    "a builtin: key or a custom-field key — lower-case, starting with a letter",
  );

/**
 * Capped at 25. The custom-field cap `publish_custom_field_schema` enforces is
 * 50 per type; a single stage demanding half of them is a design problem rather
 * than a request to serve.
 */
export const requiredFieldKeys = z.array(requiredFieldKey).max(25);

/**
 * Where a new stage goes in the pipeline order.
 *
 * `sequence` is what the exit gate compares to decide forward from backward
 * (migration `0056`), so placement is a behavioural choice and not cosmetic —
 * hence naming the anchor rather than asking the caller for a raw number in the
 * common case. The seeded pipelines are numbered in tens for exactly this
 * reason: there is room to insert between two stages without renumbering, and
 * renumbering is impossible for the API anyway (see the module comment).
 */
export const stagePlacement = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("end") }),
  z.object({ kind: z.literal("after"), stageCode }),
  z.object({ kind: z.literal("before"), stageCode }),
  /** The escape hatch, for an admin who is reading the numbers deliberately. */
  z.object({ kind: z.literal("sequence"), sequence: z.number().int().min(0).max(100_000) }),
]);
export type StagePlacement = z.infer<typeof stagePlacement>;

/**
 * Add a stage to an existing type.
 *
 * **No `isEntry`.** `work_order_stage_type_entry_uq` is a partial unique index
 * on `(work_order_type_id) WHERE is_entry AND active`, so a second entry stage
 * raises `23505` — and the flag cannot be cleared from the incumbent, because
 * `is_entry` is not in `tb_app`'s UPDATE column list. An entry stage is
 * therefore chosen once, when the type is created.
 */
export const addStageBody = strictObject({
  code: stageCode,
  name: stageName,
  placement: stagePlacement,
  /** `work_order_stage_closed_chk` — `NOT is_closed OR is_completed`. */
  isCompleted: z.boolean().default(false),
  isClosed: z.boolean().default(false),
  requiredFields: requiredFieldKeys.default([]),
  accruesLabour: z.boolean().default(false),
  /**
   * Entering this stage takes the asset off the road (migration 0216).
   *
   * Defaults false, matching the column, so a stage added without mentioning it
   * behaves exactly as stages did before the flag existed. The seeded pipelines
   * set it on `in_bay` and `on_site`; a user-authored stage says so for itself.
   */
  marksAssetUnavailable: z.boolean().default(false),
  idempotencyKey,
});
export type AddStageBody = z.infer<typeof addStageBody>;

/**
 * Rename a stage, re-gate it, retire it, or restore it.
 *
 * The five fields are exactly `tb_app`'s UPDATE grant on `work_order_stage`,
 * which is not a coincidence: this body cannot offer a field the connection
 * cannot write. `marksAssetUnavailable` joined both together in migration 0216 —
 * the grant and this schema are edited in one change or the checkbox raises
 * 42501 the first time somebody ticks it.
 *
 * `active: false` is the retirement path. It **succeeds** while open work orders
 * are sitting in the stage (Eden, 24 August 2026 — "deactivate, leave
 * occupants"): the stage goes inactive, nothing new may enter it, and the work
 * already there moves out normally. What the caller gets instead of a refusal is
 * a `stage_still_occupied` warning naming how many. See `assessStageRetirement`
 * in `apps/api/src/read/pipeline-plan.ts` for the two refusals that survive.
 */
export const editStageBody = strictObject({
    name: stageName.optional(),
    requiredFields: requiredFieldKeys.optional(),
    active: z.boolean().optional(),
    accruesLabour: z.boolean().optional(),
    marksAssetUnavailable: z.boolean().optional(),
    idempotencyKey,
  })
  .refine(
    (body) =>
      body.name !== undefined || body.requiredFields !== undefined || body.active !== undefined ||
      body.accruesLabour !== undefined || body.marksAssetUnavailable !== undefined,
    {
      message:
        "Send at least one of name, requiredFields, active, accruesLabour or marksAssetUnavailable.",
    },
  );
export type EditStageBody = z.infer<typeof editStageBody>;

/**
 * Add a legal move between two stages of one type.
 *
 * Stage **ids**, not codes: the stages already exist here, unlike at creation
 * time where ids do not exist until the insert. The composite foreign key
 * `(from_stage_id, work_order_type_id)` is what makes an edge into another
 * type's stage structurally impossible, and passing ids is what lets the
 * database check it.
 */
export const addTransitionBody = strictObject({
  fromStageId: z.string().uuid(),
  toStageId: z.string().uuid(),
  /**
   * `work_order_stage_transition_allowed_roles_chk` requires at least one, and
   * `transition_work_order` enforces `p_actor_role = ANY(allowed_roles)`. An
   * empty grant is an edge nobody could ever take.
   */
  allowedRoles: z.array(z.enum(userRoles)).min(1).max(userRoles.length),
  idempotencyKey,
});
export type AddTransitionBody = z.infer<typeof addTransitionBody>;

/**
 * Re-grant an edge, or retire/restore it. Mirrors the UPDATE grant, again.
 *
 * The edge is named by its `(fromStageId, toStageId)` pair in the path rather
 * than by a transition id, because `work_order_stage_transition_edge_uq` makes
 * the pair unique per type and because `GET /config` hands the client the pair
 * and not the id. Neither end is in the body: re-pointing an edge would rewrite
 * what a past move meant, and `work_order_status_event` records the move rather
 * than the edge it travelled along.
 */
export const editTransitionBody = strictObject({
    allowedRoles: z.array(z.enum(userRoles)).min(1).max(userRoles.length).optional(),
    active: z.boolean().optional(),
    idempotencyKey,
  })
  .refine((body) => body.allowedRoles !== undefined || body.active !== undefined, {
    message: "Send at least one of allowedRoles or active.",
  });
export type EditTransitionBody = z.infer<typeof editTransitionBody>;

/**
 * Something the edit is allowed to do and probably should not.
 *
 * Warnings are **not** refusals, and the distinction is load-bearing for an
 * incremental editor: a stage added before its transitions exist is stranded for
 * as long as it takes to add them, so refusing a transiently broken graph would
 * make it impossible to build one a piece at a time. What the API must not do is
 * stay quiet about it.
 */
export type PipelineWarningCode =
  /** Nothing can reach a stage from the entry stage. */
  | "stage_unreachable"
  /** A gate that can never fire, because no forward edge leaves the stage. */
  | "gate_never_fires"
  /**
   * A gate whose backing signal only a vendor collector writes.
   *
   * `builtin:meter_reading` needs `asset_meter_reading` rows and
   * `builtin:location_at_open` needs `work_order.created_latitude`; both arrive
   * from Samsara, and no collector runs on a schedule anywhere yet. A person
   * cannot satisfy either by hand, so a stage gated on one blocks every forward
   * move out of itself until the collectors are armed.
   *
   * A WARNING and never a refusal, per the cold-start rule: the honest default
   * state of a collected signal is ABSENT, so a control that refuses on absence
   * is a softlock whose only cure is the activity it is refusing.
   */
  | "gate_has_no_writer"
  /**
   * A gate a person CAN satisfy, but only through a particular surface — so the
   * operator has to know that surface exists and who can reach it. Distinct from
   * `gate_has_no_writer`, which means wait for a collector: telling somebody to
   * check Settings → Collectors when the answer is "record the vendor's invoice"
   * sends them to the wrong screen.
   */
  | "gate_needs_a_surface"
  /** The new edge runs backwards in `sequence`, so the exit gate skips it. */
  | "backward_edge"
  /** The new stage sits before the entry stage, inverting what "forward" means. */
  | "sequence_before_entry"
  /** Closed work orders still point at a stage that was just retired. */
  | "history_points_here"
  /**
   * OPEN work orders are still sitting in a stage that was just retired.
   *
   * Not a refusal since 24 August 2026: Eden chose "deactivate, leave
   * occupants". They stay where they are and can still be moved on —
   * `transition_work_order` reads the stage being LEFT without an `active`
   * filter, deliberately (migration `0056`). What changes is that nothing new
   * enters the stage and nothing can move back into it.
   */
  | "stage_still_occupied"
  /**
   * Those occupants have no way out: no active transition leaves the retired
   * stage for an active target, so retiring it traps them.
   *
   * Still a warning rather than a refusal, because it is recoverable — an edge
   * may be added out of a retired stage, which is what `edge_inert` describes.
   * A refusal would only move the same trap to the moment before it is escapable.
   */
  | "occupants_stranded"
  /** The type would have no way to close, or no way to complete before closing. */
  | "endpoint_missing"
  /** An edge that survives but can never be taken. */
  | "edge_inert";

export interface PipelineWarning {
  code: PipelineWarningCode;
  message: string;
}

/** What the caller gets back after a stage was added or changed. */
export interface StageEditReceipt {
  typeId: string;
  stageId: string;
  code: string;
  name: string;
  sequence: number;
  isEntry: boolean;
  isCompleted: boolean;
  isClosed: boolean;
  active: boolean;
  accruesLabour: boolean;
  /** Entering this stage opens an `asset_downtime` interval (migration 0216). */
  marksAssetUnavailable: boolean;
  requiredFields: string[];
  warnings: PipelineWarning[];
}

/** What the caller gets back after an edge was added or changed. */
export interface TransitionEditReceipt {
  typeId: string;
  transitionId: string;
  fromStageId: string;
  toStageId: string;
  fromLabel: string;
  toLabel: string;
  allowedRoles: string[];
  active: boolean;
  /**
   * `forward` means the target sits later in `sequence`, which is the only case
   * the exit gate fires on (migration `0056`). A `backward` edge is never gated
   * — rework happens because the job is not finished.
   */
  direction: "forward" | "backward";
  warnings: PipelineWarning[];
}
